import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  buildWordSentenceExpectedAnswer,
  type WordCharacterBack,
} from "@-/dev.improve.haniverse.lib.sentence.context";
import type { GraphemeMorphemeMnemonicsPanelMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  DrillPageContext,
  type GraphemeDrillType,
  type WordDrillType,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import { useCallback, useRef } from "react";
import {
  answerVariants,
  normalizeAnswer,
  normalizeTypedMeaningAnswer,
  normalizeTypedMeaningAnswerVariants,
} from "@-/dev.improve.haniverse.lib.gloss.answer-variants";

export function normalizeDrillAnswer(value: string, drillTypeKind: DrillType["kind"]): string {
  return drillTypeKind === "meaning" ? normalizeTypedMeaningAnswer(value) : normalizeAnswer(value);
}

export function isDrillAnswerInExpectedSet(
  input: string,
  expectedAnswerSet: ReadonlySet<string>,
  drillTypeKind: DrillType["kind"],
): boolean {
  return expectedAnswerSet.has(normalizeDrillAnswer(input, drillTypeKind));
}

function addTypedMeaningAnswerVariants(set: Set<string>, answer: string) {
  for (const variant of answerVariants(answer)) {
    for (const normalizedVariant of normalizeTypedMeaningAnswerVariants(variant)) {
      if (normalizedVariant.length > 0) {
        set.add(normalizedVariant);
      }
    }
  }
}

/**
 * A grapheme drill's expected answers, from its narrowed mnemonics slice: the drilled reading for a
 * pronunciation card, the gloss's variants for a meaning card.
 */
export function buildGraphemeDrillExpectedAnswerSet({
  drillType,
  slice,
}: {
  drillType: GraphemeDrillType;
  slice: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
}): ReadonlySet<string> {
  const set = new Set<string>();
  if (drillType.kind === "pronunciation") {
    const entry = slice.pinyinList.find((e) => e.toneNumbersPinyin === drillType.pinyin);
    if (entry !== undefined) {
      set.add(normalizeAnswer(entry.toneNumbersPinyin));
    }
    return set;
  }
  if (slice.back !== undefined && slice.back.length > 0) {
    addTypedMeaningAnswerVariants(set, slice.back);
  }
  return set;
}

/**
 * A word drill's expected answers. A `word-sentence` card's answer is its characters' backs in
 * order, space-joined — the same per-character meanings the revealed back renders as tiles, so
 * what is graded is exactly what is then shown. Deliberately NOT a morpheme's own meaning variants:
 * for a word that is a different string than the sum of its characters. The sentence-production
 * kinds grade through their own state machine, so they expect no typed answer here.
 */
export function buildWordDrillExpectedAnswerSet({
  characterBacks,
  drillType,
}: {
  characterBacks: readonly WordCharacterBack[];
  drillType: WordDrillType;
}): ReadonlySet<string> {
  const set = new Set<string>();
  if (drillType.kind !== "word-sentence") {
    return set;
  }
  const expectedAnswer = buildWordSentenceExpectedAnswer(characterBacks);
  if (expectedAnswer !== null) {
    addTypedMeaningAnswerVariants(set, expectedAnswer);
  }
  return set;
}

/** The expected answers the drill's branch built for this card. */
export function useExpectedAnswerSet(): ReadonlySet<string> {
  return useContextSelectors(DrillPageContext, (c) => c.expectedAnswerSet)[0];
}

export function useIsCorrectAnswer(): (input: string) => boolean {
  const expectedAnswerSet = useExpectedAnswerSet();
  const [drillTypeKind] = useContextSelectors(DrillPageContext, (c) => c.drillType.kind);
  return useCallback(
    (input: string) => isDrillAnswerInExpectedSet(input, expectedAnswerSet, drillTypeKind),
    [drillTypeKind, expectedAnswerSet],
  );
}

function useSubmitAnswerMutation({ onReveal }: { onReveal: () => Promise<void> | void }) {
  const [answerInputState, setAnswerInputState] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.answerInputState,
    (c) => c.setAnswerInputState,
  );
  const [drillTypeKind] = useContextSelectors(DrillPageContext, (c) => c.drillType.kind);

  const expectedAnswerSet = useExpectedAnswerSet();

  // Auto-submit's `setInputOverride` lets the caller hand off the freshly-typed
  // value because the LearnLayoutContext mirror is one render stale at the moment a
  // typed-character handler fires.
  const inputOverrideRef = useRef<string | null>(null);

  const submitAnswerMutation = useMutation({
    mutationFn: async () => {
      const override = inputOverrideRef.current;
      inputOverrideRef.current = null;
      const rawInput = override ?? answerInputState.input;
      const trimmed = rawInput.trim();

      if (trimmed.length === 0) {
        setAnswerInputState((prev) => ({
          ...prev,
          answer: "",
          presetRating: null,
          validationState: null,
        }));
        await onReveal();
        return;
      }

      const isCorrect = isDrillAnswerInExpectedSet(trimmed, expectedAnswerSet, drillTypeKind);

      if (isCorrect) {
        setAnswerInputState((prev) => ({
          ...prev,
          answer: trimmed,
          presetRating: 4,
          validationState: "correct",
        }));
        await onReveal();
        return;
      }

      setAnswerInputState((prev) => ({
        ...prev,
        answer: trimmed,
        presetRating: 1,
        validationState: "incorrect",
      }));
      await onReveal();
    },
    mutationKey: ["submitDrillAnswer"],
  });

  const setInputOverride = useCallback((value: string | null) => {
    inputOverrideRef.current = value;
  }, []);

  return Object.assign(submitAnswerMutation, { setInputOverride });
}

export function useSubmitAnswer() {
  const revealMutation = useRevealMutation();

  return useSubmitAnswerMutation({
    onReveal: () => revealMutation.mutateAsync(),
  });
}
